import React, { useState } from 'react';
import { Phone, MapPin, Clock, Star, Instagram, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import ContactForm from '../components/ContactForm';
import Gallery from '../components/Gallery';
import Menu from '../components/Menu';
import { mockData } from '../utils/mockData';

const Home = () => {
  const [showContactForm, setShowContactForm] = useState(false);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <nav className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-slate-800">Mar d'Areia</h1>
          <div className="hidden md:flex gap-6">
            <button onClick={() => scrollToSection('about')} className="text-slate-700 hover:text-blue-600 transition-colors">
              Sobre
            </button>
            <button onClick={() => scrollToSection('menu')} className="text-slate-700 hover:text-blue-600 transition-colors">
              Menu
            </button>
            <button onClick={() => scrollToSection('gallery')} className="text-slate-700 hover:text-blue-600 transition-colors">
              Galeria
            </button>
            <button onClick={() => scrollToSection('contact')} className="text-slate-700 hover:text-blue-600 transition-colors">
              Contacto
            </button>
          </div>
          <a href={`tel:${mockData.phone}`} className="md:hidden">
            <Button variant="outline" size="sm">
              <Phone className="w-4 h-4" />
            </Button>
          </a>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_db5f6d10-80a4-4c8d-b63b-ce208e1aca3f/artifacts/xyopmepz_Screenshot%202026-01-03%20at%2014.55.34.png"
            alt="Peixe grelhado no carvão"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/60 via-slate-900/40 to-slate-900/70"></div>
        </div>
        
        <div className="relative z-10 text-center text-white px-4 max-w-4xl">
          <h2 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
            Mar d'Areia
          </h2>
          <p className="text-xl md:text-2xl mb-4 text-stone-100 font-light">
            Peixe fresco, grelhado da forma tradicional
          </p>
          <p className="text-lg md:text-xl mb-8 text-stone-200">
            No coração da Ericeira, Portugal
          </p>
          
          <div className="flex items-center justify-center gap-2 mb-8">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
              ))}
            </div>
            <span className="text-lg font-semibold">{mockData.rating}</span>
            <span className="text-stone-300">({mockData.reviewCount}+ avaliações)</span>
          </div>

          <div className="flex flex-wrap gap-4 justify-center">
            <Button
              size="lg"
              onClick={() => scrollToSection('menu')}
              className="bg-blue-600 hover:bg-blue-700 text-white transition-all"
            >
              Ver Menu
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => scrollToSection('contact')}
              className="bg-white/10 backdrop-blur-sm border-white text-white hover:bg-white hover:text-slate-900 transition-all"
            >
              Como Chegar
            </Button>
            <a href={`tel:${mockData.phone}`}>
              <Button
                size="lg"
                variant="outline"
                className="bg-white/10 backdrop-blur-sm border-white text-white hover:bg-white hover:text-slate-900 transition-all"
              >
                <Phone className="w-4 h-4 mr-2" />
                Ligar para Reservar
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl font-bold mb-6 text-slate-800">
                  Uma Tradição de Marisco Autêntico
                </h2>
                <p className="text-lg text-slate-600 mb-4 leading-relaxed">
                  Bem-vindo ao Mar d'Areia, onde o peixe fresco encontra os métodos tradicionais de cozinha portuguesa. 
                  O nosso restaurante familiar serve o melhor marisco grelhado na Ericeira há anos.
                </p>
                <p className="text-lg text-slate-600 mb-4 leading-relaxed">
                  Todas as manhãs, selecionamos a captura mais fresca dos pescadores locais. O nosso peixe é exibido 
                  à entrada, grelhado no carvão da forma tradicional, e servido com genuína hospitalidade portuguesa.
                </p>
                <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                  Sem frescuras, sem pretensões – apenas marisco honesto e delicioso num ambiente caloroso e acolhedor 
                  que parece estar em casa.
                </p>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-blue-600" />
                    <span className="text-slate-700">Abre às {mockData.openingTime}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-blue-600" />
                    <span className="text-slate-700">Ericeira, Portugal</span>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img
                  src="https://images.pexels.com/photos/2233733/pexels-photo-2233733.jpeg"
                  alt="Grelhado tradicional a carvão"
                  className="w-full h-[500px] object-cover rounded-lg shadow-xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Menu Section */}
      <Menu />

      {/* Gallery Section */}
      <Gallery />

      {/* Reviews Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12 text-slate-800">
            O Que Dizem os Nossos Clientes
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {mockData.reviews.map((review, index) => (
              <Card key={index} className="border-stone-200 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex mb-3">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-slate-700 mb-4 italic">"{review.text}"</p>
                  <p className="text-sm font-semibold text-slate-800">— {review.author}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact & Location Section */}
      <section id="contact" className="py-20 bg-stone-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-12 text-slate-800">
            Visite-nos
          </h2>
          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            {/* Contact Information */}
            <div>
              <Card className="mb-6 border-stone-200">
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold mb-6 text-slate-800">Informações de Contacto</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-slate-800">Morada</p>
                        <p className="text-slate-600">{mockData.address}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Phone className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-slate-800">Telefone</p>
                        <a href={`tel:${mockData.phone}`} className="text-blue-600 hover:underline">
                          {mockData.phone}
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-slate-800">Horário</p>
                        <p className="text-slate-600">Abre às {mockData.openingTime}</p>
                        <p className="text-sm text-slate-500 mt-1">Apenas para comer no local</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Instagram className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-slate-800">Siga-nos</p>
                        <a
                          href={mockData.instagram}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline"
                        >
                          @mardareia
                        </a>
                      </div>
                    </div>
                  </div>

                  <Button
                    className="w-full mt-6 bg-blue-600 hover:bg-blue-700"
                    size="lg"
                    onClick={() => window.open(mockData.googleMapsUrl, '_blank')}
                  >
                    <MapPin className="w-4 h-4 mr-2" />
                    Como Chegar
                  </Button>
                </CardContent>
              </Card>

              <Card className="border-stone-200">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-4 text-slate-800">Como Chegar</h3>
                  <ul className="space-y-2 text-slate-600">
                    <li>• Localizado no coração da Ericeira</li>
                    <li>• 10 minutos a pé da praia</li>
                    <li>• Estacionamento disponível nas proximidades</li>
                    <li>• Fácil acesso de Lisboa (45 min de carro)</li>
                  </ul>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div>
              <Card className="border-stone-200">
                <CardContent className="p-6">
                  <h3 className="text-2xl font-bold mb-2 text-slate-800">Envie-nos uma Mensagem</h3>
                  <p className="text-slate-600 mb-6">
                    Tem dúvidas ou quer fazer uma reserva? Entre em contacto connosco.
                  </p>
                  <ContactForm />
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Map */}
          <div className="mt-12 max-w-6xl mx-auto">
            <div className="w-full h-[400px] rounded-lg overflow-hidden shadow-lg">
              <iframe
                src={mockData.mapEmbedUrl}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Localização Mar d'Areia"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div>
              <h3 className="text-2xl font-bold mb-4">Mar d'Areia</h3>
              <p className="text-stone-300">
                Marisco tradicional português no coração da Ericeira
              </p>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">Links Rápidos</h4>
              <div className="space-y-2">
                <button onClick={() => scrollToSection('about')} className="block text-stone-300 hover:text-white transition-colors">
                  Sobre
                </button>
                <button onClick={() => scrollToSection('menu')} className="block text-stone-300 hover:text-white transition-colors">
                  Menu
                </button>
                <button onClick={() => scrollToSection('gallery')} className="block text-stone-300 hover:text-white transition-colors">
                  Galeria
                </button>
                <button onClick={() => scrollToSection('contact')} className="block text-stone-300 hover:text-white transition-colors">
                  Contacto
                </button>
              </div>
            </div>

            <div>
              <h4 className="font-bold mb-4">Contacto</h4>
              <div className="space-y-2 text-stone-300">
                <p>{mockData.address}</p>
                <a href={`tel:${mockData.phone}`} className="block hover:text-white transition-colors">
                  {mockData.phone}
                </a>
                <a
                  href={mockData.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 hover:text-white transition-colors"
                >
                  <Instagram className="w-4 h-4" />
                  Siga-nos no Instagram
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-stone-700 mt-8 pt-8 text-center text-stone-400">
            <p>© 2024 Mar d'Areia. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
